import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, FileCheck2, Shield, AlertCircle } from 'lucide-react';
import { publicLinkError } from './errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ESignPage() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typedSig, setTypedSig] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [auditHash, setAuditHash] = useState(null);

  useEffect(() => {
    axios.get(`${API}/los/signature-requests/public/${token}`)
      .then((r) => { setReq(r.data); if (r.data.status === 'signed') setDone(true); })
      .catch((e) => setError(publicLinkError(e, 'Signature request not found or expired')))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!consent || !typedSig || !req) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/los/signature-requests/public/${token}/sign`, {
        signer_name: req.signer_name,
        signer_email: req.signer_email,
        typed_signature: typedSig,
        consent: true,
        user_agent: navigator.userAgent,
      });
      setAuditHash(res.data.audit_hash);
      setDone(true);
    } catch (e) {
      setError(publicLinkError(e, 'Signing failed'));
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F8FC' }}>
      <Loader2 size={30} className="animate-spin text-slate-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F7F8FC' }}>
      <div className="max-w-md text-center bg-white rounded-2xl p-8 border border-slate-200">
        <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
        <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Cannot open document</div>
        <p className="text-slate-500 mt-2 text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #F7F8FC 0%, #EEF0F8 100%)' }}>
      {/* Header */}
      <header className="border-b" style={{ background: '#070B5F', borderColor: 'rgba(107,168,46,0.35)' }}>
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #6BA82E 50%, transparent 100%)' }} />
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold text-white">Top Choice Lending</div>
            <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#A6E56C' }}>Secure Document Signing</div>
          </div>
          <div className="text-right">
            <div className="text-white/70 text-[11px] uppercase tracking-widest">Signing as</div>
            <div className="text-white font-semibold text-[13px]">{req.signer_name}</div>
            <div className="text-white/60 text-[11px]">{req.signer_email}</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {done ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center" data-testid="e-sign-success">
            <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: '#F3FAE8' }}>
              <CheckCircle size={42} style={{ color: '#6BA82E' }} />
            </div>
            <div className="font-serif text-2xl font-bold" style={{ color: '#070B5F' }}>All Signed</div>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              Thank you, <b>{req.signer_name}</b>. Your signature has been recorded with a tamper-proof audit trail.
            </p>
            {auditHash && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-mono" style={{ background: 'rgba(107,168,46,0.08)', color: '#3a5717' }}>
                <Shield size={11} /> Audit hash: {auditHash.substring(0, 24)}…
              </div>
            )}
          </div>
        ) : (
          <>
            {/* PDF preview */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <FileCheck2 size={14} style={{ color: '#6BA82E' }} />
                <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>Disclosure Package</div>
                <span className="text-[11px] text-slate-400">· {req.forms?.length || 0} forms</span>
              </div>
              {req.pdf_public_token && (
                <iframe
                  src={`${API}/los/documents/public/${req.pdf_public_token}`}
                  title="disclosure-pdf"
                  className="w-full"
                  style={{ height: '520px' }}
                />
              )}
              <div className="p-6 text-[13px] text-slate-700 space-y-2 leading-relaxed">
                <p><b>Forms included in this package:</b></p>
                <ol className="list-decimal ml-5 space-y-1">
                  {(req.forms || []).map((f) => (
                    <li key={f}>{f.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase())}</li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Sign box */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'linear-gradient(90deg, #6BA82E 0%, #8bc94f 100%)' }} />
              <div className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Sign this package</div>
              <p className="text-slate-500 text-[13px] mt-1">
                By typing your full legal name below and clicking "I agree & sign", you consent to sign these documents electronically pursuant to the E-SIGN Act.
              </p>
              <div className="mt-4">
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Type Your Full Legal Name</label>
                <input
                  value={typedSig}
                  onChange={(e) => setTypedSig(e.target.value)}
                  placeholder={req.signer_name}
                  data-testid="e-sign-name-input"
                  className="mt-1 w-full h-14 px-4 border-b-2 text-2xl outline-none focus:border-b-4 transition-all"
                  style={{
                    borderColor: typedSig ? '#6BA82E' : '#cbd5e1',
                    fontFamily: 'cursive',
                    background: '#fefce8',
                  }}
                />
              </div>
              <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="e-sign-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-green-600"
                />
                <span className="text-[12px] text-slate-600 leading-relaxed">
                  I agree to receive and sign this document electronically. I understand my signature is legally binding and will be recorded with an audit trail (IP, timestamp, SHA-256 hash).
                </span>
              </label>
              <button
                onClick={submit}
                disabled={!consent || !typedSig || submitting}
                data-testid="e-sign-submit"
                className="mt-5 w-full h-12 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition"
                style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <FileCheck2 size={16} />}
                {submitting ? 'Recording signature…' : 'I agree & sign'}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
